import * as React from "react"
import { usePopOverState } from "@digits-shared/hooks/usePopOverState"
import { BadgeContextProvider } from "src/frontend/components/Shared/Contexts/BadgeContext"

const NOOP = () => {}

interface NavSidebarContextValue {
  variant: "default" | "collapsed"
  isHeaderMenuOpen: boolean
  showHeaderMenu: () => void
  hideHeaderMenu: () => void
  toggleHeaderMenu: () => void
}

export const NavSidebarContext = React.createContext<NavSidebarContextValue>({
  variant: "default",
  isHeaderMenuOpen: false,
  showHeaderMenu: NOOP,
  hideHeaderMenu: NOOP,
  toggleHeaderMenu: NOOP,
})

/**
 * Provides the state and actions for the NavSidebar.
 */
export const NavSidebarProvider: React.FC<
  React.PropsWithChildren<{ variant?: "default" | "collapsed" }>
> = ({ children, variant = "default" }) => {
  const {
    isPopOverOpen: isHeaderMenuOpen,
    showPopOver: showHeaderMenu,
    hidePopOver: hideHeaderMenu,
  } = usePopOverState({
    waitToOpen: 350,
    waitToClose: 1000,
  })

  const toggleHeaderMenu = React.useCallback(
    () => (isHeaderMenuOpen ? hideHeaderMenu() : showHeaderMenu()),
    [isHeaderMenuOpen, hideHeaderMenu, showHeaderMenu]
  )

  const value = React.useMemo(
    () => ({
      variant,
      isHeaderMenuOpen,
      showHeaderMenu,
      hideHeaderMenu,
      toggleHeaderMenu,
    }),
    [variant, isHeaderMenuOpen, showHeaderMenu, hideHeaderMenu, toggleHeaderMenu]
  )

  return (
    <BadgeContextProvider skipActionItems={!isHeaderMenuOpen}>
      <NavSidebarContext.Provider value={value}>{children}</NavSidebarContext.Provider>
    </BadgeContextProvider>
  )
}
